import React, { useState, useEffect } from 'react'
import { getFamilyOrders } from '@/api/foodOrders'
import { apiClient } from '@/utils/api'
import { format } from 'date-fns'
import { CheckCircle2, XCircle, Clock, ChevronDown, ChevronUp, AlertCircle } from 'lucide-react'
import toast from 'react-hot-toast'
import { getErrorMessage } from '../utils/helpers'

interface FoodOrderItem {
  item_name: string
  quantity: number
  price: number
  customizations?: any
}

interface FoodOrder {
  id: string
  user_id: string
  user_name: string
  order_date: string
  delivery_date: string
  vendor_id: string
  vendor_name: string
  vendor_type: string
  order_items: FoodOrderItem[]
  total_amount: number
  status: string
  payment_status: string
  special_instructions: string | null
  created_at: string
  updated_at: string
}

export function ParentFoodOrderApproval() {
  const [orders, setOrders] = useState<FoodOrder[]>([])
  const [loading, setLoading] = useState(false)
  const [expandedOrders, setExpandedOrders] = useState<Set<string>>(new Set())
  const [selectedDate, setSelectedDate] = useState(format(new Date(), 'yyyy-MM-dd'))
  const [processingOrders, setProcessingOrders] = useState<Set<string>>(new Set())

  useEffect(() => {
    loadOrders()
  }, [selectedDate])

  const loadOrders = async () => {
    try {
      setLoading(true)
      const data = await getFamilyOrders(selectedDate)

      // Backend returns grouped object: { "Vendor Name": [{order}, ...], ... }
      // Flatten it into a single array
      let flatOrders: FoodOrder[] = []
      if (data && typeof data === 'object') {
        for (const vendorName in data) {
          const vendorOrders = data[vendorName]
          if (Array.isArray(vendorOrders)) {
            flatOrders = flatOrders.concat(vendorOrders.map((order: any) => ({
              id: order.id,
              user_id: '', // Not provided by backend
              user_name: order.ordered_by || '',
              order_date: selectedDate,
              delivery_date: selectedDate,
              vendor_id: '', // Not provided by backend
              vendor_name: order.vendor_name || vendorName,
              vendor_type: order.vendor_type || '',
              order_items: order.order_items || [],
              total_amount: order.total_amount || 0,
              status: order.status || 'pending',
              payment_status: order.payment_status || 'unpaid',
              special_instructions: order.special_instructions,
              created_at: new Date().toISOString(), // Not provided by backend
              updated_at: new Date().toISOString() // Not provided by backend
            })))
          }
        }
      }

      setOrders(flatOrders)
    } catch (error) {
      console.error('Failed to load orders:', error)
      toast.error('Failed to load food orders')
    } finally {
      setLoading(false)
    }
  }

  const toggleExpanded = (orderId: string) => {
    const newExpanded = new Set(expandedOrders)
    if (newExpanded.has(orderId)) {
      newExpanded.delete(orderId)
    } else {
      newExpanded.add(orderId)
    }
    setExpandedOrders(newExpanded)
  }

  const approveOrder = async (orderId: string) => {
    try {
      setProcessingOrders(prev => new Set(prev).add(orderId))
      await apiClient.put(`/api/food-orders/orders/${orderId}/status`, {
        status: 'approved'
      })
      toast.success('Order approved')
      await loadOrders()
    } catch (error: any) {
      console.error('Failed to approve order:', error)
      toast.error(getErrorMessage(error, 'Failed to approve order'))
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev)
        newSet.delete(orderId)
        return newSet
      })
    }
  }

  const rejectOrder = async (orderId: string) => {
    try {
      setProcessingOrders(prev => new Set(prev).add(orderId))
      await apiClient.put(`/api/food-orders/orders/${orderId}/status`, {
        status: 'rejected'
      })
      toast.success('Order rejected')
      await loadOrders()
    } catch (error: any) {
      console.error('Failed to reject order:', error)
      toast.error(getErrorMessage(error, 'Failed to reject order'))
    } finally {
      setProcessingOrders(prev => {
        const newSet = new Set(prev)
        newSet.delete(orderId)
        return newSet
      })
    }
  }

  const pendingOrders = orders.filter(o => o.status === 'pending')
  const approvedOrders = orders.filter(o => o.status === 'approved')
  const rejectedOrders = orders.filter(o => o.status === 'rejected')

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-yellow-100 text-yellow-800">
            <Clock className="w-4 h-4" />
            Pending
          </span>
        )
      case 'approved':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
            <CheckCircle2 className="w-4 h-4" />
            Approved
          </span>
        )
      case 'rejected':
        return (
          <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
            <XCircle className="w-4 h-4" />
            Rejected
          </span>
        )
      default:
        return <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800">{status}</span>
    }
  }

  const renderOrder = (order: FoodOrder) => {
    const isExpanded = expandedOrders.has(order.id)
    const isProcessing = processingOrders.has(order.id)

    return (
      <div key={order.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div
          className="p-4 cursor-pointer hover:bg-gray-50"
          onClick={() => toggleExpanded(order.id)}
        >
          <div className="flex items-start justify-between mb-2">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="font-semibold text-gray-900">{order.user_name}</h3>
                {getStatusBadge(order.status)}
              </div>
              <p className="text-sm text-gray-600">{order.vendor_name}</p>
              <p className="text-xs text-gray-500">
                Ordered: {format(new Date(order.created_at), 'PPp')}
              </p>
            </div>
            <div className="text-right">
              <p className="text-lg font-bold text-blue-600">${order.total_amount.toFixed(2)}</p>
              <p className="text-sm text-gray-600">{order.order_items.length} items</p>
            </div>
          </div>

          <div className="flex items-center justify-between">
            <button className="text-sm text-blue-600 hover:text-blue-700 flex items-center gap-1">
              {isExpanded ? (
                <>
                  <ChevronUp className="w-4 h-4" />
                  Hide details
                </>
              ) : (
                <>
                  <ChevronDown className="w-4 h-4" />
                  Show details
                </>
              )}
            </button>
          </div>
        </div>

        {isExpanded && (
          <div className="border-t border-gray-200 p-4 bg-gray-50">
            <div className="space-y-3 mb-4">
              {order.order_items.map((item, idx) => (
                <div key={idx} className="flex justify-between items-start">
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">
                      {item.quantity}x {item.item_name}
                    </p>
                    {item.customizations && Object.keys(item.customizations).length > 0 && (
                      <p className="text-sm text-gray-600">
                        Customizations: {JSON.stringify(item.customizations)}
                      </p>
                    )}
                  </div>
                  <p className="font-medium text-gray-900">${(item.price * item.quantity).toFixed(2)}</p>
                </div>
              ))}
            </div>

            {order.special_instructions && (
              <div className="mb-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm font-medium text-blue-900 mb-1 flex items-center gap-1">
                  <AlertCircle className="w-4 h-4" />
                  Special Instructions:
                </p>
                <p className="text-sm text-blue-800">{order.special_instructions}</p>
              </div>
            )}

            {order.status === 'pending' && (
              <div className="flex gap-3">
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    approveOrder(order.id)
                  }}
                  disabled={isProcessing}
                  className="flex-1 py-2 bg-green-600 text-white font-medium rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <CheckCircle2 className="w-5 h-5" />
                      Approve
                    </>
                  )}
                </button>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    rejectOrder(order.id)
                  }}
                  disabled={isProcessing}
                  className="flex-1 py-2 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {isProcessing ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5" />
                      Reject
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold text-gray-900">Food Order Approvals</h2>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading orders...</p>
        </div>
      ) : (
        <>
          {/* Pending Orders */}
          {pendingOrders.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Pending Approval ({pendingOrders.length})
              </h3>
              <div className="space-y-3">
                {pendingOrders.map(order => renderOrder(order))}
              </div>
            </div>
          )}

          {/* Approved Orders */}
          {approvedOrders.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Approved ({approvedOrders.length})
              </h3>
              <div className="space-y-3">
                {approvedOrders.map(order => renderOrder(order))}
              </div>
            </div>
          )}

          {/* Rejected Orders */}
          {rejectedOrders.length > 0 && (
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-3">
                Rejected ({rejectedOrders.length})
              </h3>
              <div className="space-y-3">
                {rejectedOrders.map(order => renderOrder(order))}
              </div>
            </div>
          )}

          {orders.length === 0 && (
            <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
              <Clock className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">No food orders for this date</p>
            </div>
          )}
        </>
      )}
    </div>
  )
}
